import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Autocomplete, TextField } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { styled } from "@mui/material/styles";
import { Grid, Stack, Typography } from "@financeable-com-au/financeable-ui";
// @ts-expect-error: ignoring as it is existing js file
import { uploadEntityDocument, userDetailsSelector } from "@/store/slices/applicationFormSlice";
import { ApplicationFormSlice, UserDetailsState } from "@/store/slices/types/applicationFormSlice";
import { IDispatchResponse } from "@/utils/interface";
import {
  ALLOWED_DOCUMENT_TYPE,
  ERRORS_MESSAGE,
  MAX_DOCUMENT_UPLOAD_SIZE,
  SUCCESS_MESSAGE,
  VALID_DOCUMENT_TYPE_EXTENSION,
} from "../../constants";
import { openSnackbar } from "../../store/slices/snackbarSlice";
import { useGetLocalAndLenderErrors } from "../../hooks/useRequiredFields";

const ENTITY_DOCUMENT_TYPES = [
  "All ATO documents",
  "Integrated Client Account (ICA)",
  "Income Tax Account",
  "Lodgement History",
];

const Input = styled("input")({
  display: "none",
});

interface EntityWithDocuments {
  _id: string;
  documents?: { name: string }[];
}

export interface EntityOtherDocumentsProps {
  entity: EntityWithDocuments;
  applicationId: string;
  isDisableEdit?: boolean;
}

const EntityOtherDocuments = React.memo(({ entity, applicationId, isDisableEdit }: EntityOtherDocumentsProps) => {
  const dispatch = useDispatch();
  const entityId = entity?._id;
  const { entityDocumentUploadLoadingId } = useSelector<ApplicationFormSlice, UserDetailsState>(userDetailsSelector);

  const { getCustomerOrEntityLocalAndLenderError } = useGetLocalAndLenderErrors();
  const entityDocumentValidationMessage = getCustomerOrEntityLocalAndLenderError({
    localState: {},
    fieldName: `documents.[0].name_${entity._id}`,
    isEntity: true,
  });

  const [documentName, setDocumentName] = useState("");
  const [error, setError] = useState("");

  const uploadedDocNames = (entity?.documents || []).map((doc: { name: string }) => doc.name);
  const availableOptions = ENTITY_DOCUMENT_TYPES.filter((type) => !uploadedDocNames.includes(type));
  const uploadingDocument = entityDocumentUploadLoadingId === entityId;
  const disabledDocumentUpload = !documentName || isDisableEdit || uploadingDocument;

  const showEntityDocumentValidation =
    !uploadedDocNames?.find((name: string) => name === "Integrated Client Account (ICA)") &&
    typeof entityDocumentValidationMessage === "string";

  const handleUploadDocument = (event: React.ChangeEvent<HTMLInputElement>, label: string | Blob) => {
    const files = event.target.files;
    const newSelectedFiles = [];
    const formData = new FormData();

    for (let i = 0; i < (files?.length ?? 0); i++) {
      const file = files?.[i];
      if (file && ALLOWED_DOCUMENT_TYPE.includes(file.type)) {
        if (file.size <= MAX_DOCUMENT_UPLOAD_SIZE) {
          newSelectedFiles.push(file);
        } else {
          setError(ERRORS_MESSAGE.documentSizeMsg);
        }
      } else {
        setError(ERRORS_MESSAGE.documentTypeMsg);
      }
    }

    if (newSelectedFiles?.length) {
      newSelectedFiles.forEach((file) => {
        formData.append("file", file);
      });
      formData.append("applicationId", applicationId);
      formData.append("entityId", entityId);
      formData.append("name", label);
      dispatch(uploadEntityDocument({ formData })).then((res: IDispatchResponse) => {
        if (!res?.error) {
          dispatch(
            openSnackbar({
              message: res?.payload?.data?.status_message || SUCCESS_MESSAGE.fileUploadSuccess,
              intent: "success",
            }),
          );
        } else {
          dispatch(
            openSnackbar({
              message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
              intent: "danger",
            }),
          );
        }
      });
      event.target.value = "";
      setError("");
      setDocumentName("");
    }
  };

  return (
    <Grid xs={12} style={{ margin: "20px 0 30px 0" }}>
      <Grid xs={12} sm={12}>
        <Stack spacing={1} direction="row" alignItems="flex-start">
          <Autocomplete
            size="small"
            disabled={isDisableEdit}
            value={documentName || null}
            options={availableOptions}
            onChange={(_event, value) => setDocumentName(value ?? "")}
            sx={{
              width: 300,
              border: "1px solid rgba(0, 0, 0, 0.12)",
              borderRadius: "5px",
              borderColor: showEntityDocumentValidation ? "red" : "transparent",
            }}
            renderInput={(params) => <TextField {...params} label="Document type" variant="outlined" size="small" />}
          />
          <Stack direction="row" alignItems="center" spacing={2}>
            <label htmlFor={`upload-entity-document-${entityId}`}>
              <Input
                accept={VALID_DOCUMENT_TYPE_EXTENSION}
                id={`upload-entity-document-${entityId}`}
                type="file"
                value={undefined}
                disabled={disabledDocumentUpload}
                onChange={(event) => handleUploadDocument(event, documentName)}
              />
              <LoadingButton variant="contained" component="span" disabled={disabledDocumentUpload}>
                {uploadingDocument ? "Uploading..." : "Upload Document"}
              </LoadingButton>
            </label>
          </Stack>
        </Stack>
        {(showEntityDocumentValidation || error) && (
          <Stack mt={5}>
            <Typography level="body-xs" textColor="danger.200">
              {showEntityDocumentValidation ? `${entityDocumentValidationMessage || "Document"} is required` : error}
            </Typography>
          </Stack>
        )}
      </Grid>
    </Grid>
  );
});

export default EntityOtherDocuments;
